import { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const { t, language, setLanguage } = useLanguage();
  const rafRef = useRef<number | null>(null);

  const navItems = [
    { id: 'hero', label: t.nav?.home || 'Domov' },
    { id: 'about', label: t.nav?.about || 'O mně' },
    { id: 'services', label: t.nav?.services || 'Služby' },
    { id: 'pricing', label: t.nav?.pricing || 'Ceny' },
    { id: 'contact', label: t.nav?.contact || 'Kontakt' },
  ];

  const toggleLanguage = () => {
    setLanguage(language === 'cs' ? 'en' : 'cs');
  };

  const handleScroll = useCallback(() => {
    if (rafRef.current !== null) return;
    
    rafRef.current = requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 50);

      // Determine active section
      for (const item of navItems) {
        const element = document.getElementById(item.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(item.id);
            break;
          }
        }
      }

      if (scrollY < 100) {
        setActiveSection('hero');
      }

      rafRef.current = null;
    });
  }, [navItems]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleScroll]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    if (id === 'hero') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(id);
      if (element) {
        const offset = window.innerWidth < 640 ? 64 : 80;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - offset,
          behavior: 'smooth',
        });
      }
    }
  };

  // Close mobile menu on scroll
  useEffect(() => {
    const closeMenu = () => {
      if (isMobileMenuOpen) setIsMobileMenuOpen(false);
    };
    window.addEventListener('scroll', closeMenu, { passive: true });
    return () => window.removeEventListener('scroll', closeMenu);
  }, [isMobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 animate-fade-in-down ${
        isScrolled || isMobileMenuOpen
          ? 'bg-black/80 backdrop-blur-md border-b border-primary-500/20 shadow-lg'
          : 'bg-transparent'
      }`}
      style={{ animationDelay: '0s' }}
    >
      <div className="container-max px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo/Name */}
          <a
            href="#hero"
            onClick={(e) => handleNavClick(e, 'hero')}
            className="text-xl sm:text-2xl font-bold text-white hover:text-primary-400 transition-colors hover:scale-105 active:scale-95"
          >
            Martin <span className="text-primary-400">Šenk</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => handleNavClick(e, item.id)}
                className={`relative text-sm font-medium transition-all whitespace-nowrap hover:-translate-y-0.5 ${
                  activeSection === item.id
                    ? 'text-primary-400'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {item.label}
                {activeSection === item.id && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-400 rounded-full" />
                )}
              </a>
            ))}

            {/* Language Switcher - Desktop */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 bg-gray-800/50 backdrop-blur-sm border border-primary-500/30 rounded-lg px-3 py-2 text-white hover:bg-gray-700/50 hover:border-primary-400/50 transition-all duration-300 group hover:scale-105 active:scale-95"
            >
              <Globe className="w-4 h-4 text-primary-400 group-hover:rotate-180 transition-transform duration-300" />
              <span className="font-semibold text-xs uppercase tracking-wider">
                {language === 'cs' ? 'CS' : 'EN'}
              </span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-3">
            {/* Language Switcher - Mobile */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 bg-gray-800/50 backdrop-blur-sm border border-primary-500/30 rounded-lg px-2.5 py-1.5 text-white hover:bg-gray-700/50 hover:border-primary-400/50 transition-all duration-300 active:scale-90"
            >
              <Globe className="w-4 h-4 text-primary-400" />
              <span className="font-semibold text-xs uppercase tracking-wider">
                {language === 'cs' ? 'CS' : 'EN'}
              </span>
            </button>
            
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-300 hover:text-white transition-colors p-1 active:scale-90"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden border-t border-primary-500/20 overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-80 py-4' : 'max-h-0 py-0'
          }`}
        >
          <div className="flex flex-col gap-3">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => handleNavClick(e, item.id)}
                className={`text-base font-medium transition-colors py-2 px-2 rounded-lg active:scale-95 ${
                  activeSection === item.id
                    ? 'text-primary-400 bg-primary-500/10'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
